import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FaInstagram, FaEnvelope } from 'react-icons/fa';

const FooterContainer = styled.footer`
  background-color: var(--bg-dark);
  padding: 0 0 1.05rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const DeleteAccountSection = styled.div`
  width: 100%;
  text-align: center;
  padding: 1.2rem 0;
  background-color: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.95rem;
    margin: 0 0 0.8rem;
    font-weight: 400;
    padding: 0 1.5rem;
    max-width: 600px;
  }
  
  @media (max-width: 768px) {
    padding: 1.2rem 1rem;
  }
`;

const EmailButton = styled.a`
  display: inline-flex;
  align-items: center;
  background-color: var(--primary);
  color: #000;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0.5rem 1.2rem;
  border-radius: 6px;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    background-color: #00ff9d;
    color: #000;
    font-weight: 600;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const FooterContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const FooterColumn = styled.div`
  width: 100%;
`;

const FooterLogo = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.7rem;
  letter-spacing: 0.5px;
  
  span.one {
    color: var(--text-light);
    transition: color 0.3s ease;
  }
  
  span.cal {
    color: var(--primary);
  }
  
  span.ai {
    color: var(--text-light);
    transition: color 0.3s ease;
  }
  
  &:hover {
    span.one, span.ai {
      color: var(--primary);
    }
  }
`;

const FooterDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1.05rem;
  max-width: 500px;
  line-height: 1.6;
  margin-left: auto;
  margin-right: auto;
  font-size: 1.05rem;
`;

const SocialIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 0px;
  margin-bottom: 2px;
  
  a {
    color: #FFFFFF;
    font-size: 1.4rem;
    transition: all 0.3s ease;
    opacity: 0.8;
    
    &:hover {
      color: var(--primary);
      opacity: 1;
      transform: translateY(-3px);
    }
  }
`;

const FooterBottom = styled.div`
  width: 100%;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  
  p {
    margin-top: 0.7rem;
    font-size: 0.85rem;
    opacity: 0.6;
  }
`;

const PolicyLinks = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.7rem;
  flex-wrap: wrap;
  gap: 0.5rem 2rem;
  
  a {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    margin: 0;
    transition: all 0.2s ease;
    font-size: 0.95rem;
    font-weight: 400;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      width: 0;
      height: 1px;
      bottom: -3px;
      left: 0;
      background-color: var(--primary);
      transition: width 0.3s ease;
    }
    
    &:hover {
      color: var(--primary);
      
      &:after {
        width: 100%;
      }
    }
  }
  
  @media (max-width: 480px) {
    justify-content: center;
    gap: 0.5rem 1.5rem;
  }
`;

const Footer = () => {
  const { t } = useTranslation();
  
  const currentYear = new Date().getFullYear();
  
  return (
    <FooterContainer>
      <DeleteAccountSection>
        <p>{t('footer.deleteAccount', 'Если Вы хотите удалить свой аккаунт, обратитесь по email')}</p>
        <EmailButton href="mailto:support@1cal.ai">
          <FaEnvelope /> support@1cal.ai
        </EmailButton>
      </DeleteAccountSection>
      <FooterContent>
        <FooterColumn>
          <FooterLogo>
            <span className="one">1</span><span className="cal">cal</span><span className="ai">.ai</span>
          </FooterLogo>
          <FooterDescription>
            {t('footer.description', 'AI-powered calorie tracking app that makes nutrition monitoring simple and accurate.')}
          </FooterDescription>
          <SocialIcons>
            <a href="https://instagram.com/1cal.ai" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <FaInstagram />
            </a>
          </SocialIcons>
        </FooterColumn>
        
        <FooterBottom>
          <PolicyLinks>
            <a href="/pricing">{t('footer.pricing', 'Pricing')}</a>
            <a href="/privacy">{t('footer.privacy', 'Privacy Policy')}</a>
            <a href="/terms">{t('footer.terms', 'Terms of Service')}</a>
            <a href="/subscription">{t('footer.subscription', 'Subscription Terms')}</a>
            <a href="/refund">{t('footer.refund', 'Refund Policy')}</a>
          </PolicyLinks>
          <p>
            © {currentYear} 1cal.ai. {t('footer.rights', 'All rights reserved.')}
          </p>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer; 